import { api } from '@/integrations/clients/carAuction/carAuctionClient';
import { isNativeCapacitorApp } from '@/integrations/clients/carAuction/nativeAuth';
import { CapacitorHttp } from '@capacitor/core';

interface ApiResponse<T> {
  success: boolean;
  data: T;
}

interface PresignedUrlResponse {
  uploadUrl: string;
  expiresAt: string;
  s3Key: string;
}

interface CreateMediaResponse {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
  s3Key: string;
  status: 'UPLOADING' | 'UPLOADED' | 'FAILED';
  actorId?: string;
  createdAt: string;
}

interface ProfilePictureUrlResponse {
  url: string | null;
}

export class MediaService {
  /**
   * Request presigned URL for file upload
   */
  static async requestPresignedUploadUrl(
    mediaType: 'profile' | 'news' | 'campaign' | 'contract' | 'sellerContract' | 'invoice' | 'carTest' | 'vehicleCard' | 'feedback' | 'bugReport' | 'supportGuide' | 'companyLogo' | 'dealerPortalCustomer' | 'dealerPortalVehicle',
    parentId: string,
    metadata: {
      originalName: string;
      mimeType: string;
      sizeBytes: number;
    },
    subPath?: string
  ): Promise<PresignedUrlResponse> {
    const response = await api.post<ApiResponse<PresignedUrlResponse>>('/media/upload/request', {
      mediaType,
      parentId,
      metadata,
      subPath,
    });
    // API client unwraps response.data, so response is already { success: true, data: PresignedUrlResponse }
    if (!response.success || !response.data) {
      throw new Error(`Failed to request presigned URL: ${!response.success ? 'Unsuccessful response' : 'Missing data'}`);
    }
    return response.data;
  }

  /**
   * Upload file to S3 using presigned URL.
   * Native: direct PUT via CapacitorHttp (base64 body).
   */
  static async uploadFileToS3(uploadUrl: string, file: File): Promise<void> {
    if (isNativeCapacitorApp()) {
      await MediaService.putFileDirectToS3Native(uploadUrl, file);
      return;
    }

    await MediaService.putFileDirectToS3(uploadUrl, file);
  }

  /** CapacitorHttp patched fetch cannot send raw binary; use native request + base64. */
  private static async putFileDirectToS3Native(
    uploadUrl: string,
    file: File,
  ): Promise<void> {
    const contentType = file.type || 'application/octet-stream';
    const base64 = await MediaService.fileToBase64(file);
    const response = await CapacitorHttp.request({
      url: uploadUrl,
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      data: base64,
      dataType: 'file',
    });
    if (response.status < 200 || response.status >= 300) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }
  }

  private static fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Failed to read file as base64'));
          return;
        }
        const comma = result.indexOf(',');
        resolve(comma >= 0 ? result.slice(comma + 1) : result);
      };
      reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
      reader.readAsDataURL(file);
    });
  }

  private static async putFileDirectToS3(
    uploadUrl: string,
    file: File,
  ): Promise<void> {
    const contentType = file.type || 'application/octet-stream';
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: {
        'Content-Type': contentType,
      },
      body: file,
    });
    if (!response.ok) {
      throw new Error(`S3 upload failed: ${response.status}`);
    }
  }

  /**
   * Options for createMediaAfterUpload. Use named fields so mediaSize is never sent as bugReportId.
   */
  static async createMediaAfterUpload(
    s3Key: string,
    metadata: {
      originalName: string;
      mimeType: string;
      sizeBytes: number;
    },
    options?: {
      actorId?: string;
      newsId?: string;
      campaignId?: string;
      contractId?: string;
      sellerContractId?: string;
      invoiceId?: string;
      carTestPictureId?: string;
      vehicleCardImageId?: string;
      vehicleCardDocumentId?: string;
      dealerPortalCustomerDocumentId?: string;
      dealerPortalVehicleDocumentId?: string;
      feedbackId?: string;
      bugReportId?: string;
      companyId?: string;
      mediaSize?: "PREVIEW" | "FULL_SIZE" | null;
    }
  ): Promise<CreateMediaResponse> {
    const opts = options ?? {};
    const response = await api.post<ApiResponse<CreateMediaResponse>>('/media/upload/complete', {
      s3Key,
      metadata,
      mediaSize: opts.mediaSize ?? null,
      actorId: opts.actorId,
      newsId: opts.newsId,
      campaignId: opts.campaignId,
      contractId: opts.contractId,
      sellerContractId: opts.sellerContractId,
      invoiceId: opts.invoiceId,
      carTestPictureId: opts.carTestPictureId,
      vehicleCardImageId: opts.vehicleCardImageId,
      vehicleCardDocumentId: opts.vehicleCardDocumentId,
      dealerPortalCustomerDocumentId: opts.dealerPortalCustomerDocumentId,
      dealerPortalVehicleDocumentId: opts.dealerPortalVehicleDocumentId,
      feedbackId: opts.feedbackId,
      bugReportId: opts.bugReportId,
      companyId: opts.companyId,
    });
    // API client unwraps response.data, so response is already { success: true, data: CreateMediaResponse }
    if (!response.success || !response.data) {
      throw new Error(`Failed to create media: ${!response.success ? 'Unsuccessful response' : 'Missing data'}`);
    }
    return response.data;
  }

  /**
   * Update Media status after upload completion/failure
   */
  static async updateMediaStatus(mediaId: string, status: 'UPLOADED' | 'FAILED'): Promise<void> {
    await api.put(`/media/${mediaId}/status`, { status });
  }

  /**
   * Get profile picture URL (works for both users and admins)
   */
  static async getProfilePictureUrl(expiresIn?: number, userType: 'user' | 'admin' = 'user'): Promise<string | null> {
    try {
      const endpoint = userType === 'admin' 
        ? `/admin/profile/picture${expiresIn ? `?expiresIn=${expiresIn}` : ''}`
        : `/users/profile/picture${expiresIn ? `?expiresIn=${expiresIn}` : ''}`;
      
      const response = await api.get<ApiResponse<ProfilePictureUrlResponse>>(endpoint);
      // API client unwraps response.data, so response is already { success: true, data: ProfilePictureUrlResponse }
      if (!response.success || !response.data) {
        return null;
      }
      return response.data.url;
    } catch (error: any) {
      // If 404 or no profile picture, return null
      if (error?.status === 404 || error?.status === 400) {
        return null;
      }
      throw error;
    }
  }

  /**
   * Delete profile picture (works for both users and admins)
   */
  static async deleteProfilePicture(userType?: 'user' | 'admin'): Promise<void> {
    const endpoint = userType === 'admin' 
      ? '/admin/profile/picture'
      : '/users/profile/picture';
    await api.delete(endpoint);
  }

  /**
   * Get download URL for a media file.
   * When preferFullSize is true, backend may return the FULL_SIZE sibling URL for dealer portal vehicle images (for lightbox).
   */
  static async getDownloadUrl(
    mediaId: string,
    expiresIn?: number,
    preferFullSize?: boolean
  ): Promise<string> {
    const params = new URLSearchParams();
    if (expiresIn != null) params.set("expiresIn", String(expiresIn));
    if (preferFullSize === true) params.set("preferFullSize", "true");
    const query = params.toString();
    const response = await api.get<ApiResponse<{ downloadUrl: string; expiresAt: string }>>(
      `/media/${mediaId}/download${query ? `?${query}` : ""}`
    );
    // API client unwraps response.data, so response is already { success: true, data: { downloadUrl: string; expiresAt: string } }
    if (!response.success || !response.data) {
      throw new Error(`Failed to get download URL: ${!response.success ? "Unsuccessful response" : "Missing data"}`);
    }
    return response.data.downloadUrl;
  }

  /**
   * Delete media file
   */
  static async deleteMedia(mediaId: string, fkType: string): Promise<void> {
    await api.delete(`/media/${mediaId}`, {
      data: { fkType },
    });
  }
}

